import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Grid, List, ListItem, ListItemText } from '@material-ui/core';

import Dialog from '../components/Dialog';
import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DateTimePicker from '../components/DateTimePicker';
import Checkbox from '../components/Checkbox';
import Select from '../components/Select';
import MultiSelect from '../components/MultiSelect';
import ColorPicker from '../components/ColorPicker';

import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useDiaryData from '../hooks/useDiaryData';
// import useMainContext from '../hooks/useMainContext';

import dateTimeUtils from '../components/DateTimePicker/utils';
import { maxWidth } from '../components/configs';

// import tries from '../api/tries';
import events from '../api/events';

import {
  colorOptions,
  repeatOptions,
  repeatPeriodOptions,
  eventStatusOptions,
  appointmentCountOptions,
} from '../configs/events';

const queryParamKey = 'create-event';
const dateFormat = 'YYYY-MM-DD';
const dateTimeFormat = 'YYYY-MM-DD HH:mm';
const emptyValue = appointmentCountOptions[0].value;

const generateDefaultState = selectedDate => {
  const currentMoment = moment();
  const defaultMoment = moment(selectedDate)
    .hour(currentMoment.hour())
    .minute(currentMoment.minute());

  return {
    name: '',
    contact: '',
    status: '0',
    appointmentCount: appointmentCountOptions[0].value,
    startAt: dateTimeUtils.generateDefaultValue(
      defaultMoment.format(dateTimeFormat),
      30,
      true
    ),
    endAt: dateTimeUtils.generateDefaultValue(
      moment(defaultMoment).add(1, 'hour').format(dateTimeFormat),
      30,
      true
    ),
    isAllDay: false,
    remarks: '',
    repeat: repeatOptions[0].value,
    repeatFrequency: 1,
    repeatPeriod: repeatPeriodOptions[0].value,
    repeatEndDate: null,
    color: colorOptions[0],
    // tries: [],
  };
};

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  customRepeatWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-around',
  },
  submitButton: {
    width: '100%',
    marginTop: 20,
    marginBottom: 20,
    color: '#fff',
    borderRadius: 6,
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  list: {
    marginTop: 10,
    width: '100%',
    backgroundColor: '#eaeaea',
    borderRadius: theme.shape.borderRadius,
  },
  dialogContentWrapper: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginTop: 10,
    marginBottom: 10,
  },
}));

function CreateEventDialog() {
  const classes = useStyles();
  const [conflictEvents, setConflictEvents] = useState([]);
  // eslint-disable-next-line
  const [diary, _, handleSetDate, handleManualFetch] = useDiaryData();
  const [disabled, setDisabled] = useState(false);
  const [queryParams, setQueryParams] = useQueryParams();
  const [state, _setState] = useState(generateDefaultState(diary.selectedDate));
  const [error, setError] = useState({});
  const handleSnackbarChange = useSnackbarText();
  // const { handleChange: handleMainContextChange } = useMainContext();

  const queryValue = queryParams[queryParamKey];

  const setState = useCallback(newState => {
    _setState(prevState => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const handleChange = type => (event, value) => {
    if (value !== undefined) setState({ [type]: value });
    else setState({ [type]: event.target.value });
  };

  const handleDateTimeChange = type => (event, value) => {
    setState({ [type]: value });
    if (!state.isAllDay) {
      if (type === 'startAt') {
        setState({ endAt: moment(value).add(1, 'hour') });
      } else if (type === 'endAt') {
        const startMoment = moment(state.startAt);
        const endMoment = moment(value);
        if (endMoment.diff(startMoment) < 0) {
          setState({ startAt: moment(endMoment).add(-1, 'hour') });
        }
      }
    }
  };

  const handleCheckboxChange = (event, value) => {
    setState({ isAllDay: value });
  };

  const handleValueCheck = () => {
    let noError = true;
    let error = {};
    if (!state.name) {
      noError = false;
      error.name = 'Invalid value.';
    }
    setError(error);
    return noError;
  };

  const handleRepeatEndCheckbox = (event, value) => {
    if (value) {
      setState({ repeatEndDate: null });
    } else {
      setState({ repeatEndDate: moment().add(1, 'week').format(dateFormat) });
    }
  };

  const handleMultiSelectChange = event => {
    const newValue = event.target.value;
    setState({ status: newValue.length ? newValue.join(',') : '0' });
  };

  const handleSubmitPress = async () => {
    if (!handleValueCheck()) {
      return handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
    }

    const isRepeat = Boolean(state.repeat !== repeatOptions[0].value);
    if (!isRepeat) {
      setDisabled(true);
      const { data, success } = await events.checkConflict({
        eventID: null,
        startAt: new Date(moment(state.startAt).format()),
        endAt: new Date(moment(state.endAt).format()),
        isAllDay: state.isAllDay,
      });
      setDisabled(false);
      if (success) {
        if (data.conflictEvents.length) {
          setQueryParams({ conflict: 'true' });
          setConflictEvents(data.conflictEvents);
        } else {
          handleSubmit();
        }
      } else {
        return handleSnackbarChange({ text: data.message, variant: 'error' });
      }
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setQueryParams({ conflict: null });
    const isRepeat = Boolean(state.repeat !== repeatOptions[0].value);
    const isCustomRepeat = Boolean(state.repeat === repeatOptions[5].value);

    setDisabled(true);
    const eventResult = await events.createEvent({
      name: state.name,
      contact: state.contact,
      status: state.status,
      appointmentCount:
        state.appointmentCount === emptyValue ? null : state.appointmentCount,
      startAt: new Date(moment(state.startAt).format()),
      endAt: new Date(moment(state.endAt).format()),
      isAllDay: state.isAllDay,
      remarks: state.remarks,
      isRepeat: isRepeat,
      isCustomRepeat: isCustomRepeat,
      repeatFrequency: isRepeat
        ? isCustomRepeat
          ? state.repeatFrequency
          : 1
        : null,
      repeatPeriod: isRepeat
        ? isCustomRepeat
          ? state.repeatPeriod
          : state.repeat
        : null,
      repeatEndDate: isRepeat
        ? state.repeatEndDate && new Date(state.repeatEndDate)
        : null,
      color: state.color,
    });

    if (!eventResult.success) {
      setDisabled(false);
      return handleSnackbarChange({
        text: eventResult.data.message,
        variant: 'error',
      });
    }

    setDisabled(false);
    handleSnackbarChange({ text: 'Event created', variant: 'success' });
    handleManualFetch();
    return setQueryParams({ [queryParamKey]: null });
  };

  useEffect(() => {
    _setState(generateDefaultState(diary.selectedDate));
    if (!queryValue) setQueryParams({ conflict: null });
  }, [queryValue, diary.selectedDate, setQueryParams]);

  return (
    <FullScreenDialog title="Create New Event" queryParamKey={queryParamKey}>
      <div className={classes.container}>
        <TextField
          label="Name*"
          value={state.name}
          onChange={handleChange('name')}
          error={Boolean(error.name)}
          helperText={error.name}
        />

        <TextField
          label="Contact"
          value={state.contact}
          onChange={handleChange('contact')}
          error={Boolean(error.contact)}
          helperText={error.contact}
        />

        <MultiSelect
          label="Status"
          value={state.status ? state.status.split(',') : []}
          options={eventStatusOptions
            // disable `Non Face To Face`
            .filter(option => option.value !== '12')}
          onChange={handleMultiSelectChange}
        />

        <Grid container spacing={1}>
          <Grid xs={3} item>
            <Select
              label="Apt"
              options={appointmentCountOptions}
              value={state.appointmentCount}
              onChange={handleChange('appointmentCount')}
            />
          </Grid>

          <Grid xs={9} item>
            <TextField
              label="Remarks"
              value={state.remarks}
              onChange={handleChange('remarks')}
              error={Boolean(error.remarks)}
            />
          </Grid>
        </Grid>

        <DateTimePicker
          label="Start Date"
          isDayHighlighted={date =>
            date === moment(state.endAt).format(dateFormat)
          }
          value={state.startAt}
          onChange={handleDateTimeChange('startAt')}
          showTime={!state.isAllDay}
        />

        <DateTimePicker
          label="End Date"
          isDayHighlighted={date =>
            date === moment(state.startAt).format(dateFormat)
          }
          value={state.endAt}
          onChange={handleDateTimeChange('endAt')}
          showTime={!state.isAllDay}
        />

        <Checkbox
          label="All Day"
          value={state.isAllDay}
          onChange={handleCheckboxChange}
        />

        <Select
          label="Repeat"
          options={repeatOptions}
          value={state.repeat}
          onChange={handleChange('repeat')}
        />

        {/* CUSTOM Repeat */}
        {state.repeat === repeatOptions[5].value && (
          <div className={classes.customRepeatWrapper}>
            <TextField
              label="Repeat Frequency"
              value={state.repeatFrequency}
              onChange={handleChange('repeatFrequency')}
              error={Boolean(error.repeatFrequency)}
              helperText={error.repeatFrequency}
              type="number"
              maxWidth="45%"
            />

            <Select
              label="Repeat Period"
              options={repeatPeriodOptions}
              value={state.repeatPeriod}
              onChange={handleChange('repeatPeriod')}
              maxWidth="45%"
            />
          </div>
        )}

        {state.repeat !== repeatOptions[0].value && (
          <Checkbox
            label="Repeat never end"
            value={!state.repeatEndDate}
            onChange={handleRepeatEndCheckbox}
          />
        )}

        {Boolean(state.repeatEndDate) && (
          <DateTimePicker
            label="Repeat End Date"
            value={state.repeatEndDate}
            onChange={handleDateTimeChange('repeatEndDate')}
          />
        )}

        <ColorPicker
          value={state.color}
          options={colorOptions}
          onChange={handleChange('color')}
        />

        {/* <List className={classes.list} dense>
					<ListItem onClick={handleCreateTryOpen} button>
						<ListItemText primary="+ Create Tracking" />
					</ListItem>
					{state.tries.map(e => (
						<ListItem key={e.tryID} onClick={handleUpdateTryOpen(e.tryID)} button>
							<ListItemText primary={e.name} />
							<ListItemSecondaryAction>
								<IconButton onClick={() => setQueryParams({ tryDeleteID: e.tryID })}>
									<DeleteIcon />
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List> */}

        <Button
          className={classes.submitButton}
          onClick={handleSubmitPress}
          disabled={disabled}
        >
          Create
        </Button>
      </div>

      <Dialog title="Conflict with following events" queryParamKey="conflict">
        <List dense>
          {conflictEvents.map((e, i) => (
            <ListItem key={i}>
              <ListItemText
                primary={e.name}
                secondary={`${e.date} (${
                  e.isAllDay ? 'All Day' : e.startTime + '-' + e.endTime
                })`}
              />
            </ListItem>
          ))}
        </List>

        <div className={classes.dialogContentWrapper}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Proceed
          </Button>
          <Button onClick={() => setQueryParams({ conflict: null })}>
            Cancel
          </Button>
        </div>
      </Dialog>

      {/* <Dialog title="Delete this tracking?" queryParamKey="tryDeleteID">
				<div className={classes.dialogContentWrapper}>
					<Button 
						variant="contained" 
						color="primary" 
						onClick={handleDeleteTry(queryParams.tryDeleteID)}
					>
						Delete
					</Button>
					<Button onClick={() => setQueryParams({ tryDeleteID: null })}>
						Cancel
					</Button>
				</div>
			</Dialog> */}
    </FullScreenDialog>
  );
}

export default CreateEventDialog;
